import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useEffect, useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import z from "zod";
import { ActiveAdminTable } from "@/components/tables/admins-active";
import { PendingAdminTable } from "@/components/tables/admins-pending";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ActiveAdminsQuery } from "./__generated__/ActiveAdminsQuery.graphql";
import type { administradores_ActiveAdmins$key } from "./__generated__/administradores_ActiveAdmins.graphql";
import type { administradores_PendingAdmins$key } from "./__generated__/administradores_PendingAdmins.graphql";
import type { administradoresQuery } from "./__generated__/administradoresQuery.graphql";
import type { PendingAdminsQuery } from "./__generated__/PendingAdminsQuery.graphql";

const defaultValues = {
	view: "active",
	page: 1,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/configuracoes/administradores",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(["active", "pending"]).default("active"),
			page: z.number().min(1).default(1),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function ActiveAdmins({ club }: { club: administradores_ActiveAdmins$key }) {
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const [, startTransitionActionUpdate] = useTransition();
	const navigate = useNavigate({ from: Route.fullPath });

	const [admins, refetch] = useRefetchableFragment<
		ActiveAdminsQuery,
		administradores_ActiveAdmins$key
	>(
		graphql`
		fragment administradores_ActiveAdmins on Club
			@refetchable(queryName: "ActiveAdminsQuery")
			@argumentDefinitions(
				first: { type: "Int"}
				last: { type: "Int"}
				after: { type: "String"}
				before: { type: "String"}
			)
		{
			admins(first: $first, after: $after, before: $before, last: $last) {
				edges {
					node {
						id
						username
						email
						avatarFallback
						photo {
							signedUrl
						}
						memberSince(club: $slug)
					}
					cursor
				}
				pageInfo {
					hasNextPage
					hasPreviousPage
					totalCount
					startCursor
					endCursor
				}
			}
		}
	`,
		club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	return (
		<ActiveAdminTable
			data={
				admins?.admins?.edges.filter(Boolean).map(({ node }) => ({
					user: {
						id: node.id,
						username: node.username,
						email: node.email,
						avatarFallback: node.avatarFallback,
						photo: node.photo,
					},
					joinedAt: node.memberSince,
				})) || []
			}
			isPending={isPending}
			totalCount={admins?.admins?.pageInfo.totalCount || 0}
			hasNextPage={Boolean(admins?.admins?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(admins?.admins?.pageInfo.hasPreviousPage)}
			loadNext={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
				})
			}
			loadPrevious={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
				})
			}
			startIndex={
				admins?.admins?.edges[0]
					? cursorToOffset(admins?.admins?.edges[0].cursor) + 1
					: 1
			}
			endIndex={
				admins?.admins?.edges[admins?.admins?.edges.length - 1]
					? cursorToOffset(
							admins?.admins?.edges[admins?.admins?.edges.length - 1].cursor,
						) + 1
					: 1
			}
			refetch={() =>
				startTransitionActionUpdate(() => {
					refetch(
						{
							after:
								page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
						},
						{ fetchPolicy: "network-only" },
					);
				})
			}
		/>
	);
}

function PendingAdmins({ club }: { club: administradores_PendingAdmins$key }) {
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const [, startTransitionActionUpdate] = useTransition();
	const navigate = useNavigate({ from: Route.fullPath });

	const [invites, refetch] = useRefetchableFragment<
		PendingAdminsQuery,
		administradores_PendingAdmins$key
	>(
		graphql`
		fragment administradores_PendingAdmins on Club
			@refetchable(queryName: "PendingAdminsQuery")
			@argumentDefinitions(
				first: { type: "Int"}
				last: { type: "Int"}
				after: { type: "String"}
				before: { type: "String"}
			)
		{
			pendingAdminsList: admins(status: PENDING, first: $first, after: $after, before: $before, last: $last) {
				edges {
					node {
						id
						email
						id
						username
						avatarFallback
						photo {
							signedUrl
						}
					}
					cursor
				}
				pageInfo {
					hasNextPage
					hasPreviousPage
					totalCount
					startCursor
					endCursor
				}
			}
		}
	`,
		club,
	);

	const adminInvites = invites?.pendingAdminsList?.edges.filter((edge) =>
		Boolean(edge?.node),
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	return (
		<PendingAdminTable
			data={
				adminInvites?.map(({ node }) => ({
					id: node.id,
					email: node.email,
					user: node
						? {
								id: node.id,
								username: node.username,
								avatarFallback: node.avatarFallback,
								photo: node.photo,
							}
						: null,
				})) || []
			}
			isPending={isPending}
			totalCount={invites?.pendingAdminsList?.pageInfo.totalCount || 0}
			hasNextPage={Boolean(invites?.pendingAdminsList?.pageInfo.hasNextPage)}
			hasPreviousPage={Boolean(
				invites?.pendingAdminsList?.pageInfo.hasPreviousPage,
			)}
			loadNext={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
				})
			}
			loadPrevious={() =>
				startTransition(() => {
					navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
				})
			}
			startIndex={
				adminInvites?.[0] ? cursorToOffset(adminInvites[0].cursor) + 1 : 0
			}
			endIndex={
				adminInvites?.length
					? cursorToOffset(adminInvites[adminInvites.length - 1].cursor) + 1
					: 0
			}
			refetch={() =>
				startTransitionActionUpdate(() => {
					refetch(
						{
							after:
								page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
						},
						{ fetchPolicy: "network-only" },
					);
				})
			}
		/>
	);
}

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { view } = useSearch({ from: Route.id });
	const navigate = useNavigate({ from: Route.fullPath });
	const [, startTransition] = useTransition();

	const data = useLazyLoadQuery<administradoresQuery>(
		graphql`
		query administradoresQuery(
			$slug: String!
			$first: Int
			$last: Int
			$after: String
			$before: String
		) {
			club(slug: $slug) {
				id
				pendingAdmins: admins(status: PENDING, first: 1) {
					pageInfo {
						totalCount
					}
				}
				activeAdmins: admins(status: ACTIVE, first: 1) {
					pageInfo {
						totalCount
					}
				}
				...administradores_ActiveAdmins @arguments(
					first: $first
					last: $last
					after: $after
					before: $before
				)
				...administradores_PendingAdmins @arguments(
					first: $first
					last: $last
					after: $after
					before: $before
				)
			}
		}
		`,
		{
			slug: club?.slug,
			first: 15,
			after: undefined,
		},
	);

	const pendingCount = data.club?.pendingAdmins?.pageInfo.totalCount || 0;
	const activeCount = data.club?.activeAdmins?.pageInfo.totalCount || 0;

	return (
		<div className="p-6">
			<Tabs
				defaultValue="active"
				value={view}
				className="flex flex-col gap-6"
				onValueChange={(val) =>
					startTransition(() => {
						navigate({
							search: (s) => ({
								...s,
								page: 1,
								view: val as "active" | "pending",
							}),
						});
					})
				}
			>
				<div className="flex flex-row items-center justify-between">
					<TabsList>
						<TabsTrigger value="active" className="border-none">
							Ativos ({activeCount})
						</TabsTrigger>
						<TabsTrigger value="pending" className="border-none">
							Pendentes ({pendingCount})
						</TabsTrigger>
					</TabsList>
				</div>

				<TabsContent value="active">
					<ActiveAdmins club={data.club} />
				</TabsContent>
				<TabsContent value="pending">
					<PendingAdmins club={data.club} />
				</TabsContent>
			</Tabs>
		</div>
	);
}
